import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';

// Everything that determines what a gadget shows, other than its chartDef.
// Used to carry a gadget's scope, filters, date and comparison along with it
// when it is copied (or its report is converted) to a new location.
export interface GadgetCopySettings {
  scope: FilterPlateType[];
  drillDowns: FilterPlateType[];
  dateField: string;
  dateRange?: DateRangeInput;
  relativeDateRange?: RelativeDateRange;
  advancedRelativeDateRange?: AdvancedRelativeDateRange;
  comparison?: PersistedComparisonType;
  weekStartsOnOverride?: WeekStartsOn;
  isDefaultFiltersDisabled?: boolean;
  isEntityFilterEnabled?: boolean;
}

const DEFAULT_RELATIVE_DATE_RANGE: RelativeDateRange = {
  interval: 'day',
  n: 30,
  type: 'last',
  currentToDate: true,
};

/**
 * Variable plates only make sense on the dashboard which defines the variable.
 * When copying elsewhere, swap them for a fixed plate holding the variable's
 * current value, or drop them if the variable can't be found.
 */
export const toPortablePlates = ({
  plates,
  variableDrillDowns,
}: {
  plates: FilterPlateType[];
  variableDrillDowns: VariableDrillDownType[];
}): FilterPlateType[] =>
  plates.flatMap((plate): FilterPlateType[] => {
    if (!filterPlateTypeCheckers.isVariable(plate)) {
      return [plate];
    }
    const v = variableDrillDowns.find((d) => d.id === plate.variableId);
    if (!v) {
      return [];
    }
    return [{ id: plate.id, type: 'Fixed', fixedValue: v.value }];
  });

/**
 * Selected report quick filters are AND-merged into each gadget's filter
 * input, so their plates can be carried over as extra drill downs.
 */
export const quickFiltersToPlates = (
  quickFilters: LegacySavedFilter[],
): FilterPlateType[] =>
  quickFilters.flatMap((qf) => [...(qf.scope || []), ...(qf.drillDowns || [])]);

/**
 * The saved settings a report gadget renders with, mirroring
 * ReportGadgetProvider and ReportGadgetDateScopeProvider.
 */
export const resolveReportGadgetSettings = ({
  report,
  reportGadget,
}: {
  report: ReportType;
  reportGadget: ReportGadgets.ReportGadget;
}): GadgetCopySettings => {
  const gadgetScope = reportGadget.scope || [];
  const isDefaultFiltersDisabled = (() => {
    const mode = reportGadget.defaultDatasetFiltersMode;
    if (mode === 'enabled') return false;
    if (mode === 'disabled') return true;
    return (
      !!reportGadget.isDefaultFiltersDisabled ||
      !!report.isDefaultFiltersDisabled
    );
  })();

  return {
    scope: reportGadget.isDefaultFiltersDisabled
      ? gadgetScope
      : [...report.scope, ...gadgetScope],
    drillDowns: reportGadget.isDefaultFiltersDisabled
      ? []
      : report.drillDowns || [],
    dateField: reportGadget.dateField || report.dateField || 'date',
    dateRange: report.dateRange,
    relativeDateRange: report.relativeDateRange,
    advancedRelativeDateRange: report.advancedRelativeDateRange,
    comparison: reportGadget.comparison ?? report.comparison,
    weekStartsOnOverride: report.weekStartsOnOverride,
    isDefaultFiltersDisabled,
    isEntityFilterEnabled: report.isEntityFilterEnabled,
  };
};

const hasDate = (settings: GadgetCopySettings) =>
  !!settings.dateRange ||
  !!settings.relativeDateRange ||
  !!settings.advancedRelativeDateRange;

export const buildDashboardGadgetFromSettings = ({
  id,
  chartDef,
  settings,
  currentUserId,
  now,
  reportDrillDownId,
}: {
  id: string;
  chartDef: DashboardGadget['chartDef'];
  settings: GadgetCopySettings;
  currentUserId: string;
  now: string;
  // The report to drill into, e.g. the report the gadget was copied from
  reportDrillDownId?: string;
}): DashboardGadget => ({
  id,
  chartDef,
  reportDrillDownId,
  scope: settings.scope,
  drillDowns: settings.drillDowns,
  dateField: settings.dateField,
  dateRange: settings.dateRange,
  relativeDateRange: settings.relativeDateRange,
  advancedRelativeDateRange: settings.advancedRelativeDateRange,
  comparison: settings.comparison,
  weekStartsOnOverride: settings.weekStartsOnOverride,
  isDefaultFiltersDisabled: settings.isDefaultFiltersDisabled,
  isEntityFilterEnabled: settings.isEntityFilterEnabled,
  createdBy: currentUserId,
  createdOn: now,
  updatedBy: currentUserId,
  updatedOn: now,
});

/**
 * A report gadget for an existing report. Report gadgets have no drill downs
 * and always follow their report's date picker, so the drill downs are folded
 * into the gadget's scope and the date range is left to the destination.
 */
export const buildReportGadgetFromSettings = ({
  id,
  chartDef,
  settings,
  currentUserId,
  now,
}: {
  id: string;
  chartDef: ReportGadgets.ReportGadget['chartDef'];
  settings: GadgetCopySettings;
  currentUserId: string;
  now: string;
}): ReportGadgets.ReportGadget => ({
  id,
  chartDef,
  scope: [...settings.scope, ...settings.drillDowns],
  dateField: settings.dateField,
  comparison: settings.comparison,
  defaultDatasetFiltersMode: settings.isDefaultFiltersDisabled
    ? 'disabled'
    : undefined,
  createdBy: currentUserId,
  createdOn: now,
  updatedBy: currentUserId,
  updatedOn: now,
});

/**
 * The filter and date fields for a brand new report built around a single
 * copied gadget, so the report shows the gadget exactly as it was.
 */
export const buildNewReportSettings = (
  settings: GadgetCopySettings,
): Pick<
  ReportType,
  | 'scope'
  | 'drillDowns'
  | 'dateField'
  | 'dateRange'
  | 'relativeDateRange'
  | 'advancedRelativeDateRange'
  | 'comparison'
  | 'weekStartsOnOverride'
  | 'isDefaultFiltersDisabled'
  | 'isEntityFilterEnabled'
> => ({
  scope: settings.scope,
  drillDowns: settings.drillDowns,
  dateField: settings.dateField,
  dateRange: settings.dateRange,
  relativeDateRange: hasDate(settings)
    ? settings.relativeDateRange
    : DEFAULT_RELATIVE_DATE_RANGE,
  advancedRelativeDateRange: settings.advancedRelativeDateRange,
  comparison: settings.comparison,
  weekStartsOnOverride: settings.weekStartsOnOverride,
  isDefaultFiltersDisabled: settings.isDefaultFiltersDisabled,
  isEntityFilterEnabled: settings.isEntityFilterEnabled,
});
